import { Component, OnInit, Input, Output, OnChanges, SimpleChanges, forwardRef, Provider, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { AdjustmentTypeService } from '../shared/adjustment-types/adjustment-type.service';
import { AdjustmentType } from '../shared/adjustment-types/adjustment-type.model';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../shared/util/util.service';
import {
  NumberValueAccessor,
  CreateNumberValueAccessorProvider,
  CreateNumberValueValidationProvider,
} from '../../shared/widgets/value-accessors/number-value-accessor';
import { FinancialServiceService } from '../shared/financial.service';

@Component({
  selector: 'wl-adjustment-type-selector',
  templateUrl: 'adjustment-type-selector.component.html',
  providers: [
    AdjustmentTypeService,
    CreateNumberValueAccessorProvider(AdjustmentTypeSelectorComponent),
    CreateNumberValueValidationProvider(AdjustmentTypeSelectorComponent),
  ],
  standalone: false
})
export class AdjustmentTypeSelectorComponent extends NumberValueAccessor implements OnInit, OnChanges {
  @Input() includeNoneValue: boolean = true;
  @Input() noneValueDisplay: string = '';
  @Input() revenueItemNum: number = 0;
  @Input() paymentTypeId: number = 0;
  @Input() accountingPackageId: number = 0;
  @Input() accountingPackageIds: Array<number> = [];

  //for validation to know if 0 an be allowed
  @Input() required: any;

  @Output() onAdjustmentTypeChanged = new EventEmitter<AdjustmentType>();

  public _adjustmentTypeService: AdjustmentTypeService;
  public _translateService: TranslateService;
  public _utilService: UtilService;
  public _noneText: string;

  public adjustmentTypes: Array<AdjustmentType>;

  public constructor(
    adjustmentTypeService: AdjustmentTypeService,
    translateService: TranslateService,
    utilService: UtilService,
    private _financialService: FinancialServiceService
  ) {
    super();
    this._adjustmentTypeService = adjustmentTypeService;
    this._translateService = translateService;
    this._utilService = utilService;
    this._noneText = '';

    this.adjustmentTypes = new Array<AdjustmentType>();
    this.revenueItemNum = 0;
  }

  //this overrides the base class function to set
  //what it means for this this control to be valid
  isValid = (): boolean => {
    if (this._utilService.isNotNullOrUndefined(this.required)) {
      if (this.value > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  ngOnInit() {
    this.loadPaymentTypes();
  }
  ngOnChanges(changes: SimpleChanges) {
    this.loadPaymentTypes();
  }

  modelChange(data: string) {
    //the data comes thru as a string convert to int
    this.value = parseInt(data);

    this.emitOutputObject();
  }

  loadPaymentTypes = (): void => {
    if (this.revenueItemNum > 0) {
      this._adjustmentTypeService.getAdjustmentTypesForRevenueItem(this.revenueItemNum).then((data: Array<AdjustmentType>) => {
        this.adjustmentTypeDataCallBack(data);
      });
    } else if (this.accountingPackageId > 0) {
      this._adjustmentTypeService.getAdjustmentTypesForAccountingPackage(this.accountingPackageId).then((data: Array<AdjustmentType>) => {
        this.adjustmentTypeDataCallBack(data);
      });
    } else if (this.paymentTypeId > 0) {
      this._adjustmentTypeService.getAdjustmentTypesForPaymentType(this.paymentTypeId).then((data: Array<AdjustmentType>) => {
        this.adjustmentTypeDataCallBack(data);
      });
    } else if (this.accountingPackageIds.length > 0) {
      this._financialService.getAdjustmentTypesByAccountingPackages(this.accountingPackageIds).then((data: Array<AdjustmentType>) => {
        this.adjustmentTypeDataCallBack(data);
      });
    } else {
      this._adjustmentTypeService.getAdjustmentTypes().then((data: Array<AdjustmentType>) => {
        this.adjustmentTypeDataCallBack(data);
      });
    }
  };

  adjustmentTypeDataCallBack = (data: Array<AdjustmentType>): void => {
    // sort by adjustmenttype
    data = data.sort((left, right): number => {
      if (left.AdjustmentType < right.AdjustmentType) return -1;
      if (right.AdjustmentType < left.AdjustmentType) return 1;
      return 0;
    });

    this.adjustmentTypes = data;

    this.adjustmentTypeAddNone(this.adjustmentTypes).then((data: Array<AdjustmentType>): void => {
      let filter: Array<AdjustmentType> = this.adjustmentTypes.filter(item => {
        return item.AdjustmentTypeId == this.value;
      });
      if (filter.length == 0) {
        //the currently selected AdjustmentTypeId is not in the list of avail paymenttypes
        this.value = 0;
      }

      this.emitOutputObject();
    });
  };

  adjustmentTypeAddNone = (data: Array<AdjustmentType>): Promise<Array<AdjustmentType>> => {
    if (this.includeNoneValue == true) {
      let noneItem: AdjustmentType = new AdjustmentType();
      noneItem.AdjustmentTypeId = 0;

      if (this._utilService.isNotNullEmptyOrWhitespace(this.noneValueDisplay)) {
        this._noneText = this.noneValueDisplay;

        noneItem.AdjustmentType = this._noneText;
        this.adjustmentTypes.splice(0, 0, noneItem);
        return Promise.resolve(this.adjustmentTypes);
      } else {
        return this._translateService
          .get('common.none')
          .toPromise()
          .then((result: string) => {
            this._noneText = this._utilService.toTitleCase(result);
            noneItem.AdjustmentType = this._noneText;
            this.adjustmentTypes.splice(0, 0, noneItem);
            return Promise.resolve(this.adjustmentTypes);
          });
      }
    } else {
      return Promise.resolve(this.adjustmentTypes);
    }
  };

  emitOutputObject = (): void => {
    let filter: Array<AdjustmentType> = this.adjustmentTypes.filter(item => {
      return item.AdjustmentTypeId == this.value;
    });
    if (filter.length > 0) {
      //emit the selected rev item full object
      this.onAdjustmentTypeChanged.emit(filter[0]);
    }
  };
}
